import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, timer, Subscription } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { STORAGE_KEYS, LEGACY_STORAGE_KEYS } from '../../../shared/constants';

/** Autenticação OAuth2 direto no PWA, sem depender do AngularJS (mesma lógica do bfc.oauth2). */
@Injectable({
  providedIn: 'root'
})
export class StandaloneAuthService implements OnDestroy {
  private tokenSubject = new BehaviorSubject<string | null>(null);
  public token$ = this.tokenSubject.asObservable();
  private tokenRefreshSubscription?: Subscription;
  
  private readonly AUTH_CALLBACK_URL = '/index.html';
  private readonly TOKEN_STORAGE_KEY = '__pwa_standalone_token';
  private readonly TOKEN_EXPIRY_KEY = '__pwa_standalone_token_expiry';
  // Nome do lock cross-tab (Web Locks API) usado por refreshWithToken() — ver PROF-13326.
  private readonly REFRESH_LOCK_NAME = 'pwa-standalone-token-refresh';
  
  // Configurações OAuth2 (devem vir do environment ou ser configuradas)
  private readonly CLIENT_ID = this.getClientId();
  private readonly SCOPES = this.getScopes();
  private readonly AUTH_BASE_URL = this.getAuthBaseUrl();
  private readonly TOKEN_ENDPOINT = '/token';

  constructor(private http: HttpClient) {
    this.initializeToken();
    this.setupTokenRefresh();
  }

  /** Resource Owner Password Credentials Grant. */
  async loginWithCredentials(username: string, password: string): Promise<void> {
    const body = new HttpParams()
      .set('grant_type', 'password')
      .set('username', username)
      .set('password', password)
      .set('client_id', this.CLIENT_ID);

    const response = await this.http.post<any>(`${this.AUTH_BASE_URL}${this.TOKEN_ENDPOINT}`, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }).toPromise();

    if (response && response.access_token) {
      this.setToken(response.access_token, response.expires_in);
      if (response.refresh_token) {
        localStorage.setItem(this.TOKEN_STORAGE_KEY + '_refresh', response.refresh_token);
      }
      return;
    }
    throw new Error('Token não retornado');
  }

  ngOnDestroy(): void {
    if (this.tokenRefreshSubscription) {
      this.tokenRefreshSubscription.unsubscribe();
    }
  }

  private initializeToken(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('_token');

    if (tokenFromUrl) {
      // Token da URL geralmente vem sem expiração explícita, assumir 1 hora
      this.setToken(tokenFromUrl, 3600);
      return;
    }

    let storedToken = localStorage.getItem(this.TOKEN_STORAGE_KEY);
    let expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);

    if (!storedToken) {
      // Fallback de leitura na chave antiga (__pwa_bridge_token) por 1 release — TODO remover
      storedToken = localStorage.getItem(STORAGE_KEYS.BRIDGE_TOKEN) ||
        localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN);
      if (storedToken) {
        const jwtExpiry = this.getJwtExpiry(storedToken);
        if (jwtExpiry) {
          expiry = jwtExpiry.toString();
        } else {
          // Token opaco (UUID, sem exp): descartá-lo aqui causaria loop (isAuthenticated()
          // → false → checkAndInitialize() → /?pwa_auth=true → Custom Tab → repete), pois
          // setToken() reescreve o UUID sem recriar o expiry. Reusa expiry salvo antes ou
          // assume 8h — se o token realmente expirou, o servidor retorna 401 e o interceptor trata.
          expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY) ||
                   String(Date.now() + 8 * 60 * 60 * 1000);
        }
      }
    }

    if (storedToken && expiry) {
      const expiryTime = parseInt(expiry, 10);
      if (Date.now() < expiryTime) {
        this.tokenSubject.next(storedToken);
        if (!localStorage.getItem(this.TOKEN_STORAGE_KEY)) {
            this.setToken(storedToken, (expiryTime - Date.now()) / 1000);
        }
        return;
      }
    }

    this.clearToken();
  }

  private setupTokenRefresh(): void {
    this.tokenRefreshSubscription = timer(0, 5 * 60 * 1000).subscribe(() => {
      this.refreshTokenIfNeeded();
    });
  }

  private async refreshTokenIfNeeded(): Promise<void> {
    const token = this.getAccessToken();
    if (!token) {
      return;
    }

    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (!expiry) {
      return;
    }

    const expiryTime = parseInt(expiry, 10);
    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;

    if (timeUntilExpiry < 5 * 60 * 1000) {
      await this.silentRefresh();
    }
  }

  getAccessToken(): string | null {
    return this.tokenSubject.value || localStorage.getItem(this.TOKEN_STORAGE_KEY);
  }

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    const now = Date.now();

    if (!token) {
      return false;
    }

    if (!expiry) {
      return false;
    }

    const expiryTime = parseInt(expiry, 10);
    const isValid = now < expiryTime;

    return isValid;
  }

  /**
   * Inicia o fluxo de login OAuth2 independente do AngularJS.
   *
   * @param forcePrompt Quando true, adiciona prompt=login (OIDC) pra forçar
   * o auth server a exibir tela de login de verdade em vez de reautenticar
   * silenciosamente via cookie de SSO ainda válido. Usar depois de um logout
   * explícito — sem isso, "logout" viraria login automático de novo.
   * Depende do auth server suportar o parâmetro OIDC prompt; se não suportar,
   * é ignorado e o comportamento cai pro SSO silencioso padrão.
   */
  login(forcePrompt = false): void {
    const redirectUri = window.location.origin + this.AUTH_CALLBACK_URL;
    const state = this.generateState();
    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_redirect_uri', window.location.href);
    const authUrl = this.buildAuthUrl(redirectUri, state, forcePrompt);
    window.location.href = authUrl;
  }

  async handleCallback(code: string, state: string): Promise<boolean> {
    const storedState = sessionStorage.getItem('oauth_state');
    if (state !== storedState) {
      throw new Error('Estado OAuth2 inválido');
    }

    const redirectUri = window.location.origin + this.AUTH_CALLBACK_URL;
    const token = await this.exchangeCodeForToken(code, redirectUri);
    
    this.setToken(token);

    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_redirect_uri');

    // App standalone vive só na raiz do domínio: a URL original está sempre dentro do
    // próprio PWA, então só o hash precisa ser atualizado (sem reload de página).
    const originalUrl = sessionStorage.getItem('oauth_redirect_uri') || '/#/diario-classe';
    if (originalUrl.includes('#/')) {
      const currentHash = window.location.hash.substring(1);
      const targetHash = originalUrl.split('#/')[1] || 'diario-classe';

      // Só atualiza o hash se a rota atual estiver vazia/inválida; senão preserva a atual
      if (!currentHash || currentHash === '/' || currentHash === '') {
        window.location.hash = targetHash;
      }
    }
    return true;
  }

  /** Tenta refresh_token primeiro; só redireciona pro login se não houver ou falhar. */
  async silentRefresh(): Promise<string | null> {
    try {
      const newToken = await this.refreshWithToken();
      if (newToken) {
        return newToken;
      }
    } catch {
      // Continua para redirect de login
    }

    this.login();
    return null;
  }

  logout(): void {
    this.clearToken();
    window.location.href = '/';
  }

  private setToken(token: string, expiresIn = 3600): void {
    const expiryTime = Date.now() + (expiresIn * 1000);
    localStorage.setItem(this.TOKEN_STORAGE_KEY, token);
    localStorage.setItem(STORAGE_KEYS.BRIDGE_TOKEN, token); // compatibilidade com leitores diretos (interceptors)
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString());
    this.tokenSubject.next(token);
  }

  private clearToken(): void {
    localStorage.removeItem(this.TOKEN_STORAGE_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    this.tokenSubject.next(null);
  }

  /** Timestamp de expiração (ms) do campo `exp` do JWT; null se não for JWT válido. */
  private getJwtExpiry(token: string): number | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (payload.exp) {
        return payload.exp * 1000; // segundos → milissegundos
      }
      return null;
    } catch {
      return null;
    }
  }

  private buildAuthUrl(redirectUri: string, state: string, forcePrompt = false): string {
    const params = new URLSearchParams({
      client_id: this.CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      // plataforma-oauth exige scopes separados por vírgula, não espaço como no OAuth2
      // padrão — espaço sempre falha (invalid_scope ou conexão recusada pelo WAF). Mantido
      // espaço-separado em this.SCOPES (formato padrão) e convertido só aqui.
      scope: this.SCOPES.replace(/\s+/g, ','),
      state: state
    });

    if (forcePrompt) {
      params.set('prompt', 'login');
    }

    return `${this.AUTH_BASE_URL}/authorize?${params.toString()}`;
  }

  private async exchangeCodeForToken(code: string, redirectUri: string): Promise<string> {
    try {
      const body = new HttpParams()
        .set('grant_type', 'authorization_code')
        .set('code', code)
        .set('redirect_uri', redirectUri)
        .set('client_id', this.CLIENT_ID);

      const response = await this.http.post<any>(
        `${this.AUTH_BASE_URL}${this.TOKEN_ENDPOINT}`,
        body.toString(),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      ).toPromise();

      if (response?.refresh_token) {
        localStorage.setItem(this.TOKEN_STORAGE_KEY + '_refresh', response.refresh_token);
      }

      return response?.access_token || response?.accessToken || '';
    } catch (error) {
      throw new Error('Erro ao trocar código por token');
    }
  }

  /** Chave própria do fluxo OAuth2 code flow do PWA — sem fallback pro bfc-oauth2/AngularJS. */
  private getStoredRefreshToken(): string | null {
    return localStorage.getItem(this.TOKEN_STORAGE_KEY + '_refresh');
  }

  /**
   * `refreshInFlight` garante 1 refresh por vez NA MESMA aba (variável de instância).
   * Entre abas, como o backend rotaciona o refresh_token a cada uso, a aba "perdedora"
   * de uma corrida receberia invalid_grant e seria deslogada mesmo com sessão válida —
   * coordenado entre abas via Web Locks API (refreshWithTokenCrossTab).
   */
  private refreshInFlight: Promise<string | null> | null = null;

  async refreshWithToken(): Promise<string | null> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    this.refreshInFlight = this.refreshWithTokenCrossTab().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  /**
   * Coordena o refresh entre abas via Web Locks API antes de gastar o refresh_token.
   * Sem `navigator.locks` (browser muito antigo), executa direto — mesmo comportamento
   * de antes desta correção.
   */
  private async refreshWithTokenCrossTab(): Promise<string | null> {
    if (!('locks' in navigator)) {
      return this.doRefreshWithToken();
    }

    const expiryBeforeLock = localStorage.getItem(this.TOKEN_EXPIRY_KEY);

    return (navigator as any).locks.request(this.REFRESH_LOCK_NAME, async () => {
      // Outra aba pode ter renovado enquanto esperávamos o lock — se a expiração
      // avançou nesse meio-tempo, reaproveita o token novo em vez de gastar de novo
      // um refresh_token que já foi rotacionado pelo backend.
      const expiryAfterLock = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
      if (expiryAfterLock && expiryAfterLock !== expiryBeforeLock) {
        const current = this.getAccessToken();
        if (current) {
          this.tokenSubject.next(current);
          return current;
        }
      }
      return this.doRefreshWithToken();
    });
  }

  private async doRefreshWithToken(): Promise<string | null> {
    const refreshToken = this.getStoredRefreshToken();
    if (!refreshToken) {
      return null;
    }

    const clientId = this.CLIENT_ID || '4f0ee5d3-8b67-4602-aa57-54d0dba85260';

    try {
      const body = new HttpParams()
        .set('grant_type', 'refresh_token')
        .set('refresh_token', refreshToken)
        .set('client_id', clientId);

      const response = await this.http.post<any>(
        `${this.AUTH_BASE_URL}${this.TOKEN_ENDPOINT}`,
        body.toString(),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      ).toPromise();

      if (response?.access_token) {
        this.setToken(response.access_token, response.expires_in || 3600);
        if (response.refresh_token) {
          localStorage.setItem(this.TOKEN_STORAGE_KEY + '_refresh', response.refresh_token);
        }
        return response.access_token;
      }

      return null;
    } catch (error) {
      // Refresh token inválido/expirado — limpar para não tentar de novo
      localStorage.removeItem(this.TOKEN_STORAGE_KEY + '_refresh');
      return null;
    }
  }

  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) +
           Math.random().toString(36).substring(2, 15);
  }

  /**
   * Fonte real: environment.oauthClientId (mesmo valor usado pelo AngularJS). O lookup em
   * window.___bth.envs.suite['suite-ui'].authentication é fallback defensivo — nunca
   * funcionou de fato (AngularJS declara `authentication` como variável local, não
   * dentro de ___bth.envs).
   */
  private getClientId(): string {
    if (environment.oauthClientId) {
      return environment.oauthClientId;
    }
    try {
      const envs = (window as any).___bth?.envs;
      if (envs) {
        return envs.suite?.['suite-ui']?.['authentication']?.clientId || '';
      }
    } catch (e) {
      // Ignorar
    }
    return '';
  }

  /** Fonte real: environment.oauthScopes (mesmo raciocínio de getClientId()). */
  private getScopes(): string {
    if ((environment as any).oauthScopes) {
      return (environment as any).oauthScopes;
    }
    try {
      const envs = (window as any).___bth?.envs;
      if (envs) {
        return envs.suite?.['suite-ui']?.['authentication']?.scopes || 'openid profile';
      }
    } catch (e) {
      // Ignorar
    }
    return 'openid profile';
  }

  /**
   * Host absoluto do backend OAuth2 (ex: https://plataforma-oauth.test.betha.cloud/auth/oauth2).
   * Fonte real: environment.oauthBaseUrl. NÃO usar `/auth/ui/v3` (widget de login, host
   * diferente) nem path relativo — sem gateway na frente deste domínio dedicado, cai no
   * SPA fallback (404 → index.html) e causa loop infinito de redirect em login().
   */
  private getAuthBaseUrl(): string {
    if ((environment as any).oauthBaseUrl) {
      return (environment as any).oauthBaseUrl;
    }
    try {
      const envs = (window as any).___bth?.envs;
      if (envs) {
        return envs.suite?.oauth?.v1?.host || envs.suite?.['suite-ui']?.['authentication']?.baseUrl;
      }
    } catch (e) {
      // Ignorar
    }
    return 'https://plataforma-oauth.test.betha.cloud/auth/oauth2';
  }
}

